import { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatNumber } from '../utils/format';
import './Statistics.css';

interface RefLinkInfo {
  ref_id: string;
  ref_name: string;
  ref_code: string;
  commission_rate: number;
  total_deposits: number;
  balance: number;
  players_count: number;
}

interface StatisticsProps {
  statistics: any;
  refLinkFilter?: string;
  onRefLinkChange: (refLink: string | undefined) => void;
}

type Period = 'day' | 'week' | 'month';
type ChartType = 'revenue' | 'clicks' | 'registrations';

export function Statistics({ statistics, refLinkFilter, onRefLinkChange }: StatisticsProps) {
  const [period, setPeriod] = useState<Period>('week');
  const [chartType, setChartType] = useState<ChartType>('revenue');

  if (!statistics) return null;

  const refLinks: RefLinkInfo[] = statistics.refLinks || [];

  const chartData = statistics.daily.map((day: any) => ({
    date: formatChartDate(day.date),
    revenue: day.revenue,
    clicks: day.clicks,
    registrations: day.registrations,
    deposits: day.deposits,
  }));

  return (
    <div className="statistics">
      {/* Фильтр по реф ссылкам */}
      {refLinks.length > 0 && (
        <div className="reflink-filter" style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
            Фильтр по реферальной ссылке:
          </label>
          <select 
            value={refLinkFilter || 'all'}
            onChange={(e) => onRefLinkChange(e.target.value === 'all' ? undefined : e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '0.95rem',
            }}
          >
            <option value="all">Все ссылки</option>
            {refLinks.map((link) => (
              <option key={link.ref_id} value={link.ref_id}>
                {link.ref_name} ({link.players_count} игроков, {formatCurrency(link.balance)} баланс)
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="period-selector">
        <button
          className={`period-btn ${period === 'day' ? 'active' : ''}`}
          onClick={() => setPeriod('day')}
        >
          День
        </button>
        <button
          className={`period-btn ${period === 'week' ? 'active' : ''}`}
          onClick={() => setPeriod('week')}
        >
          Неделя
        </button>
        <button
          className={`period-btn ${period === 'month' ? 'active' : ''}`}
          onClick={() => setPeriod('month')}
        >
          Месяц
        </button>
      </div>

      <div className="overview-cards">
        <div className="overview-card">
          <div className="overview-icon">💵</div>
          <div className="overview-content">
            <p className="overview-label">Доход</p>
            <h3 className="overview-value">{formatCurrency(statistics.overview.totalRevenue)}</h3>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">👆</div>
          <div className="overview-content">
            <p className="overview-label">Клики</p>
            <h3 className="overview-value">{formatNumber(statistics.overview.totalClicks)}</h3>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">✍️</div>
          <div className="overview-content">
            <p className="overview-label">Регистрации</p>
            <h3 className="overview-value">{formatNumber(statistics.overview.totalRegistrations)}</h3>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">💳</div>
          <div className="overview-content">
            <p className="overview-label">Депозиты</p>
            <h3 className="overview-value">{formatNumber(statistics.overview.totalDeposits)}</h3>
          </div>
        </div>
      </div>

      <div className="chart-selector">
        <button
          className={`chart-btn ${chartType === 'revenue' ? 'active' : ''}`}
          onClick={() => setChartType('revenue')}
        >
          💰 Доход
        </button>
        <button
          className={`chart-btn ${chartType === 'clicks' ? 'active' : ''}`}
          onClick={() => setChartType('clicks')}
        >
          👆 Клики
        </button>
        <button
          className={`chart-btn ${chartType === 'registrations' ? 'active' : ''}`}
          onClick={() => setChartType('registrations')}
        >
          ✍️ Регистрации
        </button>
      </div>

      <div className="chart-container">
        <h3 className="chart-title">
          {chartType === 'revenue' && 'График дохода'}
          {chartType === 'clicks' && 'График кликов'}
          {chartType === 'registrations' && 'График регистраций'}
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#34d399" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
            />
            <YAxis 
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
              tickFormatter={(value) => {
                if (chartType === 'revenue') return `$${value}`;
                return value.toString();
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '8px',
                padding: '8px 12px',
              }}
              labelStyle={{ color: '#f1f5f9', fontSize: '12px' }}
              itemStyle={{ color: '#38bdf8', fontSize: '12px' }}
              formatter={(value: any) => {
                if (chartType === 'revenue') return formatCurrency(value);
                return formatNumber(value);
              }}
            />
            <Area
              type="monotone"
              dataKey={chartType}
              stroke={
                chartType === 'revenue' ? '#38bdf8' :
                chartType === 'clicks' ? '#a78bfa' : '#34d399'
              }
              strokeWidth={2}
              fillOpacity={1}
              fill={
                chartType === 'revenue' ? 'url(#colorRevenue)' :
                chartType === 'clicks' ? 'url(#colorClicks)' : 'url(#colorRegistrations)'
              }
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="daily-breakdown">
        <h3 className="section-title">Детальная статистика</h3>
        <div className="breakdown-list">
          {statistics.daily.slice().reverse().slice(0, 7).map((day: any) => (
            <div key={day.date} className="breakdown-item">
              <div className="breakdown-date">
                <p className="breakdown-day">{formatDayOfWeek(day.date)}</p>
                <p className="breakdown-date-text">{formatDateShort(day.date)}</p>
              </div>
              <div className="breakdown-stats">
                <div className="breakdown-stat">
                  <span className="breakdown-stat-label">Доход</span>
                  <span className="breakdown-stat-value">{formatCurrency(day.revenue)}</span>
                </div>
                <div className="breakdown-stat">
                  <span className="breakdown-stat-label">Клики</span>
                  <span className="breakdown-stat-value">{formatNumber(day.clicks)}</span>
                </div>
                <div className="breakdown-stat">
                  <span className="breakdown-stat-label">Рег.</span>
                  <span className="breakdown-stat-value">{formatNumber(day.registrations)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatChartDate(dateStr: string): string {
  const date = new Date(dateStr);
  return `${date.getDate()}/${date.getMonth() + 1}`;
}

function formatDayOfWeek(dateStr: string): string {
  const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  const date = new Date(dateStr);
  return days[date.getDay()];
}

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr);
  return `${date.getDate()}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}
